import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

//components
import { Box, styled } from '@mui/material';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import BaseButton from '../../../components/common/BaseButton';
import InputBox from '../../../components/Input/InputBox';

//utils
import api from '../../../utils/api/index';
import { setCookie } from '../../../utils/cookies';
import { useConfig } from '../../../Hooks/useConfig';
import { useAuthStatus } from '../../../store/auth/useAuthStatus';
import useUsersFacade from '../../../store/profile/useUsersFacade';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { formikErrorInput, formikErrorMessage } from '../../../utils/helper';
import { showErrorToast } from '../../../utils/toast.jsx';

const ForgotLink = styled(Link)(({ theme }) => ({
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.neutral_80
      : theme.palette.secondary.neutral_30,
  fontSize: theme.spacing(3.5),
  fontWeight: 400,
}));

const FormLogin = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const { baseDomain } = useConfig();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // zustand
  const { setToken, loginState } = useAuthStatus();
  const { fetchUsers, userInfo } = useUsersFacade();

  // Determine if we're in playground mode (not in panel)
  const subdomain = window.location.hostname.split('.')[0] || '';
  const isPanel = subdomain.includes('panel');
  const isPlayground = !isPanel;

  // formik
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },

    validationSchema: Yup.object({
      password: Yup.string().required(t('login.required_password')),
      email: Yup.string()
        .email(t('login.inValid_email'))
        .required(t('login.required_email')),
    }),

    onSubmit: (values) => {
      loginUser(values);
    },
  });

  // Function to determine the redirect URL after successful login
  const determineRedirectUrl = (userInfo, location, history) => {
    // Check if user needs to go to stepper
    const needsStepperRedirect = () => {
      if (userInfo) {
        // First check for email verification
        if (userInfo.email_verified_at === null) {
          return false; // Don't redirect to stepper if email is not verified yet
        }

        // If email is verified, check if user is a first-time user
        return userInfo.show_stepper;
      }
      return false;
    };

    // If user needs stepper, redirect there
    if (needsStepperRedirect()) {
      setCookie('showStepper', 'true', 34560000, baseDomain);
      return '/stepper';
    }

    // Otherwise, use the original redirect logic
    const urlParams = new URLSearchParams(location.search);
    const redirectParam = urlParams.get('redirect');

    if (redirectParam) {
      return `/${redirectParam}`;
    } else if (history.location.state?.from) {
      return history.location.state.from;
    } else {
      return '/';
    }
  };

  const loginUser = async (valuesLogin) => {
    setIsLoading(true);

    if (!executeRecaptcha) {
      console.error('Recaptcha has not been loaded');
      setIsLoading(false);
      return;
    }

    const recaptchaToken = await executeRecaptcha('login');

    if (!recaptchaToken) {
      showErrorToast('Verification failed. Please try again.');
      setIsLoading(false);
      return;
    }

    try {
      const login = await api.call({
        name: 'login',
        data: {
          ...valuesLogin,
          email: valuesLogin.email.toLowerCase(),
          recaptcha_token: recaptchaToken,
        },
      });

      if (login.status === 200) {
        localStorage.removeItem('redirectUrl');

        // Set token and cookie
        setCookie(
          'token',
          login.data.data.token,
          login.data.data.expires_in,
          baseDomain
        );
        setToken(login.data.data.token);

        // Store expires time
        localStorage.setItem(
          'expires_Time',
          Date.now() + login.data.data.expires_in * 1000
        );

        // Track login event
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'loginComplete',
        });

        // Fetch user info to determine redirect
        try {
          await fetchUsers(); // This will update the userInfo in the store

          // Wait a moment for the store to update, then determine redirect
          setTimeout(() => {
            const currentUserInfo = useUsersFacade.getState
              ? useUsersFacade.getState().userInfo
              : userInfo;

            // Determine redirect URL based on user info
            let targetUrl = determineRedirectUrl(
              currentUserInfo,
              location,
              history
            );

            // Append query parameters if they exist (excluding redirect param for stepper)
            const urlParams = new URLSearchParams(location.search);
            if (targetUrl !== '/stepper') {
              const queryString = urlParams.toString();
              if (queryString) {
                const separator = targetUrl.includes('?') ? '&' : '?';
                targetUrl += `${separator}${queryString}`;
              }
            } else {
              // For stepper, only preserve source parameter if it exists
              const sourceParam = urlParams.get('source');
              if (sourceParam) {
                targetUrl += `?source=${sourceParam}`;
              }
            }

            // Perform the redirection
            history.push(targetUrl);
          }, 100);
        } catch (fetchError) {
          console.error('Failed to fetch user info after login:', fetchError);
          // Fallback to default redirect logic if fetching user info fails
          const urlParams = new URLSearchParams(location.search);
          const redirectParam = urlParams.get('redirect');
          const queryString = urlParams.toString();

          let targetUrl = '';
          if (redirectParam) {
            targetUrl = `/${redirectParam}`;
          } else if (history.location.state?.from) {
            targetUrl = history.location.state.from;
          } else {
            targetUrl = '/';
          }

          if (queryString) {
            const separator = targetUrl.includes('?') ? '&' : '?';
            targetUrl += `${separator}${queryString}`;
          }

          history.push(targetUrl);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (location.search) {
      localStorage.setItem('redirectUrl', location.search);
    } else if (location?.state?.from) {
      localStorage.setItem('redirectUrl', location.state.from.replace('/', ''));
    }

    if (loginState) {
      const urlParams = new URLSearchParams(location.search);
      const redirectParam = urlParams.get('redirect');
      const sourceParam = urlParams.get('source');

      let targetUrl = '';

      if (redirectParam) {
        targetUrl = `/${redirectParam}`;
      } else if (history.location.state?.from) {
        targetUrl = history.location.state.from;
      } else {
        targetUrl = '/';
      }

      if (sourceParam) {
        const separator = targetUrl.includes('?') ? '&' : '?';
        targetUrl += `${separator}source=${sourceParam}`;
      }

      history.push(targetUrl);
    }
  }, []);

  useEffect(() => {
    if (executeRecaptcha) {
      setIsRecaptchaLoaded(true);
    }
  }, [executeRecaptcha]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <InputBox
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        ErrorInput={formikErrorInput(formik, 'email')}
        errorMassage={formikErrorMessage(formik, 'email')}
        placeholder={t('login.input_email_placeholder')}
        name="email"
        type="text"
      />
      <InputBox
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        ErrorInput={formikErrorInput(formik, 'password')}
        errorMassage={formikErrorMessage(formik, 'password')}
        placeholder={t('login.input_password_placeholder')}
        name="password"
        type="password"
        password
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mb: 10,
        }}
      >
        <ForgotLink to="/forgot-password">
          {t('login.forgot_password')}
        </ForgotLink>
      </Box>
      <BaseButton
        type="submit"
        disabled={isLoading || !isRecaptchaLoaded}
        loading={isLoading}
        sx={{ width: '100%' }}
      >
        {t('button_text.login')}
      </BaseButton>
    </form>
  );
};

export default FormLogin;
